/**
 * The `pendingUploads` store (`112-offline-drafts.md` §8, `062-images-assets.md` §5).
 *
 * **A pasted screenshot is data, and design value #1 says data is never lost.** So the blob
 * goes into IndexedDB *before* the network is touched, and it stays there until the server
 * has acknowledged the bytes. If the upload fails — offline, 500, the tab killed mid-POST —
 * the picture is still on the user's disk, the node is still in their document, and a Retry
 * affordance is still on screen. After a full browser restart it is *still* there, rendered
 * from the stored blob. That is the payoff for keeping the bytes rather than an error string.
 *
 * The row is also what gates the page's save: `hasPendingUploads()` (`offline/db.ts`) reads
 * this store, and `useAutosave` refuses to enqueue while a row for the page is not `failed`.
 * That is layer 1 of the three that keep a `blob:` src out of the database.
 */
import { db, type PendingUpload } from '@/offline/db';

export async function putPendingUpload(row: PendingUpload): Promise<void> {
  await (await db()).put('pendingUploads', row);
}

export async function getPendingUpload(clientId: string): Promise<PendingUpload | undefined> {
  return (await db()).get('pendingUploads', clientId);
}

export async function listPendingUploads(pageId: string): Promise<PendingUpload[]> {
  return (await db()).getAllFromIndex('pendingUploads', 'by-page', pageId);
}

/**
 * The upload landed. The bytes are the server's problem now — and only now.
 *
 * Deleting the row is also what un-gates the autosave, so the caller must have swapped the
 * real `/uploads/` src into the node FIRST (`062` §3 steps 6 → 7, in that order).
 */
export async function clearPendingUpload(clientId: string): Promise<void> {
  await (await db()).delete('pendingUploads', clientId);
}

/**
 * A failed attempt. The row STAYS, with the blob, forever if need be.
 *
 * `status: 'failed'` is deliberately excluded from `hasPendingUploads` — a permanently
 * failed upload must not block the page's TEXT from ever saving again. The user's words
 * outrank the picture; the picture is not lost, it is queued.
 */
export async function markUploadFailed(clientId: string, error: string): Promise<void> {
  const row = await getPendingUpload(clientId);
  if (row === undefined) return;

  await putPendingUpload({
    ...row,
    status: 'failed',
    attempts: row.attempts + 1,
    lastError: error,
    nextAttemptAt: Date.now(),
  });
}

/** The Retry button, and the reconnect drain. Puts a failed row back in the queue. */
export async function requeueUpload(clientId: string): Promise<PendingUpload | undefined> {
  const row = await getPendingUpload(clientId);
  if (row === undefined) return undefined;

  const requeued: PendingUpload = { ...row, status: 'uploading', nextAttemptAt: Date.now() };
  delete requeued.lastError;

  await putPendingUpload(requeued);
  return requeued;
}

/**
 * The ONLY way a pending upload's bytes ever leave without reaching the server: a human
 * clicking Discard, knowing exactly what is being thrown away (`062` §5.7). Any other code
 * path that deletes an unsent row is a bug.
 */
export const discardPendingUpload = clearPendingUpload;
